import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ecommerceApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'redirects' | 'sitemap' | 'connectors' | 'jobs';

/** FR-ECOM-06 + 07 — SEO and marketplace integrations */
export function IntegrationsPage() {
  const [tab, setTab] = useState<Tab>('redirects');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'redirects',  label: 'Redirects' },
    { key: 'sitemap',    label: 'Sitemap' },
    { key: 'connectors', label: 'Connectors' },
    { key: 'jobs',       label: 'Sync jobs' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Online store"
        title="Integrations & SEO"
        subtitle="URL redirects, sitemaps, and marketplace connector health."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'redirects' && <RedirectsTab />}
      {tab === 'sitemap' && <SitemapTab />}
      {tab === 'connectors' && <ConnectorsTab />}
      {tab === 'jobs' && <JobsTab />}
    </div>
  );
}

function RedirectsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deletingRedirect, setDeletingRedirect] = useState<any | null>(null);

  const sfQ = useQuery({ queryKey: ['storefronts'], queryFn: () => ecommerceApi.listStorefronts() });
  const q = useQuery({
    queryKey: ['seo-redirects'],
    queryFn: () => ecommerceApi.listRedirects(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.deleteRedirect(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['seo-redirects'] });
      setDeletingRedirect(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: any) => ecommerceApi.updateRedirect(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['seo-redirects'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" disabled={!sfQ.data?.length}
          onClick={() => setShowCreate(true)}>
          + New redirect
        </button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>Storefront</th><th>From</th><th>To</th><th>Type</th><th>Active</th><th className="text-right">Hits</th><th className="text-right w-20"></th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={7}><EmptyState message="No redirects yet." /></td></tr>
              ) : q.data.map((r: any) => (
                <tr key={r.id}>
                  <td className="text-xs text-ink-600">{r.storefront_code}</td>
                  <td className="font-mono text-xs text-ink-900">{r.from_path}</td>
                  <td className="font-mono text-xs text-ink-900">{r.to_path}</td>
                  <td>
                    <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-600 ring-ink-200">
                      {r.redirect_type}
                    </span>
                  </td>
                  <td>
                    <input type="checkbox" checked={r.is_active}
                      onChange={(e) => toggleMut.mutate({ id: r.id, isActive: e.target.checked })} />
                  </td>
                  <td className="text-right font-mono tabular-nums text-xs">{r.hit_count}</td>
                  <td className="text-right">
                    <button
                      className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                      onClick={() => setDeletingRedirect(r)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateRedirectModal storefronts={sfQ.data ?? []}
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['seo-redirects'] }); setShowCreate(false); }} />}

      {deletingRedirect && (
        <Modal
          onClose={() => setDeletingRedirect(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Commerce · Delete</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete redirect?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-mono font-semibold text-ink-900">{deletingRedirect.from_path}</span>
              {' → '}<span className="font-mono text-ink-700">{deletingRedirect.to_path}</span>?
              Existing visitors using this path will no longer be redirected.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingRedirect(null)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deletingRedirect.id)}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete redirect'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CreateRedirectModal({ storefronts, onClose, onCreated }:
    { storefronts: any[]; onClose: () => void; onCreated: () => void }) {
  const [storefrontId, setStorefrontId] = useState(storefronts[0]?.id ?? '');
  const [fromPath, setFromPath] = useState('/');
  const [toPath, setToPath] = useState('/');
  const [redirectType, setRedirectType] = useState<301 | 302>(301);
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => ecommerceApi.createRedirect({
      storefrontId, fromPath, toPath, redirectType,
      notes: notes || undefined,
    }),
    onSuccess: () => { toast.success('Created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · SEO</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New redirect</h2>
        <p className="text-sm text-ink-500 mt-1">
          Redirect an old path to a new destination. 301 is permanent (SEO passes), 302 is temporary.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Storefront</label>
          <select className="input" value={storefrontId} onChange={(e) => setStorefrontId(e.target.value)}>
            {storefronts.map((s: any) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
          </select>
        </div>
        <div>
          <label className="label">From path <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={fromPath}
            onChange={(e) => setFromPath(e.target.value)} placeholder="/old-page" />
        </div>
        <div>
          <label className="label">To path <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={toPath}
            onChange={(e) => setToPath(e.target.value)} placeholder="/new-page or https://..." />
        </div>
        <div>
          <label className="label">Redirect type</label>
          <select className="input" value={redirectType}
            onChange={(e) => setRedirectType(parseInt(e.target.value) as 301 | 302)}>
            <option value={301}>301 — Permanent (passes SEO equity)</option>
            <option value={302}>302 — Temporary</option>
          </select>
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!storefrontId || !fromPath || !toPath || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create redirect'}
        </button>
      </div>
    </Modal>
  );
}

function SitemapTab() {
  const sfQ = useQuery({ queryKey: ['storefronts'], queryFn: () => ecommerceApi.listStorefronts() });
  const [storefrontId, setStorefrontId] = useState('');
  const [sitemap, setSitemap] = useState<string | null>(null);

  const fetchMut = useMutation({
    mutationFn: () => ecommerceApi.fetchSitemap(storefrontId),
    onSuccess: (data) => { setSitemap(data as string); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="card p-4 mb-3 flex gap-3 items-end">
        <div className="flex-1">
          <label className="label">Storefront</label>
          <select className="input" value={storefrontId}
            onChange={(e) => { setStorefrontId(e.target.value); setSitemap(null); }}>
            <option value="">Select…</option>
            {sfQ.data?.filter((s: any) => s.status === 'active').map((s: any) =>
              <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
            )}
          </select>
        </div>
        <button className="btn-primary" disabled={!storefrontId || fetchMut.isPending}
          onClick={() => fetchMut.mutate()}>
          {fetchMut.isPending ? 'Generating…' : 'Generate sitemap.xml'}
        </button>
      </div>

      {sitemap && (
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-ink-100 flex items-center justify-between">
            <div className="text-xs text-ink-600">
              {(sitemap.match(/<url>/g) ?? []).length} URLs · {Math.round(sitemap.length / 1024)} KB
            </div>
            <div className="text-2xs text-ink-400 leading-relaxed">
              Public URL: <span className="font-mono">/ecommerce/storefronts/{storefrontId}/sitemap.xml</span>
            </div>
          </div>
          <pre className="bg-ink-900 text-ink-100 text-2xs font-mono px-5 py-4 max-h-96 overflow-auto whitespace-pre-wrap leading-relaxed">
            {sitemap}
          </pre>
        </div>
      )}
    </div>
  );
}

function ConnectorsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [deletingConnector, setDeletingConnector] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['integrations-connectors'],
    queryFn: () => ecommerceApi.listConnectors(),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, status }: any) => ecommerceApi.updateConnector(id, { status }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['integrations-connectors'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => ecommerceApi.deleteConnector(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['integrations-connectors'] });
      setSelected(null);
      setDeletingConnector(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New connector</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4">
          <div className="card overflow-x-auto">
            <table className="table">
              <thead><tr><th>Platform</th><th>Name</th><th>Status</th><th className="text-right">Mapped</th><th className="text-right">Jobs</th><th>Last sync</th></tr></thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={6}><EmptyState message="No connectors yet." /></td></tr>
                ) : q.data.map((c: any) => (
                  <tr key={c.id} onClick={() => setSelected(c.id)}
                    className={`cursor-pointer ${selected === c.id ? '!bg-canvas-subtle' : 'hover:bg-canvas-subtle/50'}`}>
                    <td>
                      <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-600 ring-ink-200">
                        {c.platform}
                      </span>
                    </td>
                    <td className="font-medium text-ink-900 text-sm">{c.name}</td>
                    <td><ConnectorStatusBadge status={c.status} /></td>
                    <td className="text-right font-mono tabular-nums text-xs">{c.mapped_count}</td>
                    <td className="text-right font-mono tabular-nums text-xs">{c.job_count}</td>
                    <td className="text-2xs text-ink-500 font-mono tabular-nums">
                      {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : <span className="text-ink-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            {!selected ? (
              <div className="card p-4"><EmptyState message="Select a connector." /></div>
            ) : <ConnectorDetail connectorId={selected}
                  connectors={q.data ?? []}
                  onStatusChange={(status) => updateMut.mutate({ id: selected, status })}
                  onDelete={() => setDeletingConnector(q.data?.find((c: any) => c.id === selected))}
                  onSynced={() => qc.invalidateQueries({ queryKey: ['integrations-connectors'] })} />
            }
          </div>
        </div>
      )}

      {showCreate && <CreateConnectorModal
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['integrations-connectors'] }); setShowCreate(false); }} />}

      {deletingConnector && (
        <Modal
          onClose={() => setDeletingConnector(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Commerce · Delete</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete connector?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-semibold text-ink-900">{deletingConnector.name}</span>?
              Sync history is preserved for audit purposes. The connector will stop syncing immediately.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingConnector(null)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deletingConnector.id)}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete connector'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ConnectorStatusBadge({ status }: { status: string }) {
  const cls = {
    connected:    'bg-sage-50 text-sage-700 ring-sage-200',
    disconnected: 'bg-canvas-subtle text-ink-600 ring-ink-200',
    error:        'bg-red-50 text-red-700 ring-red-200',
    disabled:     'bg-canvas-subtle text-ink-400 ring-ink-100',
  }[status] ?? 'bg-canvas-subtle text-ink-600 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function ConnectorDetail({ connectorId, connectors, onStatusChange, onDelete, onSynced }:
    { connectorId: string; connectors: any[];
      onStatusChange: (s: string) => void; onDelete: () => void; onSynced: () => void }) {
  const c = connectors.find((x) => x.id === connectorId);
  if (!c) return null;

  const syncMut = useMutation({
    mutationFn: (jobType: 'inventory'|'catalog'|'orders_pull'|'price') =>
      ecommerceApi.triggerSync(c.platform, c.id, { jobType }),
    onSuccess: (d: any) => {
      toast.success(`Sync ${d.status} · ${d.succeeded}/${d.processed} ok, ${d.failed} failed`, { duration: 6000 });
      onSynced();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="card p-4">
      <div className="flex justify-between items-start mb-3 gap-2">
        <div>
          <div className="text-2xs text-ink-400 font-mono mb-0.5">{c.platform}</div>
          <div className="font-display font-bold text-base text-ink-900">{c.name}</div>
        </div>
        <ConnectorStatusBadge status={c.status} />
      </div>

      {c.last_error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          {c.last_error}
        </div>
      )}

      <div className="bg-canvas-subtle rounded-xl p-3 mb-3 space-y-1 text-xs text-ink-600">
        <div><span className="text-ink-400">Mapped products:</span> <span className="font-mono tabular-nums font-semibold">{c.mapped_count}</span></div>
        <div><span className="text-ink-400">Sync jobs total:</span> <span className="font-mono tabular-nums font-semibold">{c.job_count}</span></div>
        {c.last_sync_at && (
          <div>
            <span className="text-ink-400">Last sync:</span>{' '}
            <span className="font-mono tabular-nums">{new Date(c.last_sync_at).toLocaleString()}</span>
            {' '}— <span className="capitalize">{c.last_sync_status}</span>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-2xs text-amber-800 mb-3 leading-relaxed">
        <strong>Framework stub:</strong> real {c.platform} API calls are not made.
        Sync jobs simulate ~5% failure rate. Wire platform-specific adapters in <code className="font-mono bg-amber-100 px-1 py-0.5 rounded">integrationService.simulateSync</code>.
      </div>

      <div className="mb-3">
        <label className="label">Status</label>
        <select className="input" value={c.status}
          onChange={(e) => onStatusChange(e.target.value)}>
          <option value="disconnected">Disconnected</option>
          <option value="connected">Connected</option>
          <option value="error">Error</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {(['inventory','catalog','price','orders_pull'] as const).map((jt) => (
          <button key={jt} className="btn-primary text-xs"
            disabled={c.status !== 'connected' || syncMut.isPending}
            onClick={() => syncMut.mutate(jt)}>
            {jt === 'orders_pull' ? 'Pull orders' : `Sync ${jt}`}
          </button>
        ))}
      </div>

      <button
        className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
        onClick={onDelete}
      >
        Delete connector
      </button>
    </div>
  );
}

function CreateConnectorModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: () => void }) {
  const sfQ = useQuery({ queryKey: ['storefronts'], queryFn: () => ecommerceApi.listStorefronts() });
  const [platform, setPlatform] = useState<'shopify'|'amazon'|'ebay'>('shopify');
  const [name, setName] = useState('');
  const [storefrontId, setStorefrontId] = useState('');
  const [credentials, setCredentials] = useState('{}');

  const mut = useMutation({
    mutationFn: () => {
      let creds;
      try { creds = JSON.parse(credentials); }
      catch { throw new Error('Credentials must be valid JSON'); }
      return ecommerceApi.createConnector({
        platform, name,
        storefrontId: storefrontId || undefined,
        credentials: creds,
      });
    },
    onSuccess: () => { toast.success('Connector created — update status to "connected" once credentials are valid'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Commerce · Connector</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New connector</h2>
        <p className="text-sm text-ink-500 mt-1">
          After creation, set status to "connected" once credentials have been validated.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Platform</label>
          <select className="input" value={platform} onChange={(e) => setPlatform(e.target.value as any)}>
            <option value="shopify">Shopify</option>
            <option value="amazon">Amazon</option>
            <option value="ebay">eBay</option>
          </select>
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="main-shop" />
        </div>
        <div>
          <label className="label">Storefront <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <select className="input" value={storefrontId} onChange={(e) => setStorefrontId(e.target.value)}>
            <option value="">—</option>
            {sfQ.data?.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Credentials (JSON)</label>
          <textarea className="input font-mono text-xs" rows={4} value={credentials}
            onChange={(e) => setCredentials(e.target.value)}
            placeholder='{"shop_domain": "...", "access_token": "..."}' />
          <div className="text-2xs text-ink-500 mt-1 leading-relaxed">
            Stored as JSONB. In production, encrypt this column. Real sync is simulated in this build.
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create connector'}
        </button>
      </div>
    </Modal>
  );
}

function JobsTab() {
  const q = useQuery({
    queryKey: ['integrations-jobs'],
    queryFn: () => ecommerceApi.listSyncJobs({ limit: 100 }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Platform</th><th>Connector</th><th>Type</th><th>Status</th>
              <th className="text-right">Processed</th>
              <th className="text-right">Success</th>
              <th className="text-right">Fail</th>
              <th>Started</th><th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {!q.data?.length ? (
              <tr><td colSpan={9}><EmptyState message="No sync jobs run yet." /></td></tr>
            ) : q.data.map((j: any) => (
              <tr key={j.id}>
                <td>
                  <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-600 ring-ink-200">
                    {j.platform}
                  </span>
                </td>
                <td className="font-medium text-ink-900 text-sm">{j.connector_name}</td>
                <td className="text-xs text-ink-600 capitalize">{j.job_type.replace(/_/g,' ')}</td>
                <td><JobStatusBadge status={j.status} /></td>
                <td className="text-right font-mono tabular-nums text-sm">{j.items_processed}</td>
                <td className="text-right font-mono tabular-nums text-sm text-sage-700 font-semibold">{j.items_succeeded}</td>
                <td className={`text-right font-mono tabular-nums text-sm font-semibold ${j.items_failed > 0 ? 'text-danger' : 'text-ink-400'}`}>
                  {j.items_failed}
                </td>
                <td className="text-2xs text-ink-500 font-mono tabular-nums whitespace-nowrap">
                  {j.started_at ? new Date(j.started_at).toLocaleString() : <span className="text-ink-400">—</span>}
                </td>
                <td className="text-2xs text-ink-500 font-mono tabular-nums whitespace-nowrap">
                  {j.completed_at ? new Date(j.completed_at).toLocaleString() : <span className="text-ink-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  const cls = {
    queued:    'bg-canvas-subtle text-ink-600 ring-ink-200',
    running:   'bg-blue-50 text-blue-700 ring-blue-200',
    completed: 'bg-sage-50 text-sage-700 ring-sage-200',
    partial:   'bg-amber-50 text-amber-800 ring-amber-200',
    failed:    'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-600 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}
